import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { selectNShowcase } from '@/store/slices/userPreferences';

import { Icon, Text, type IconName } from '../../../../components';
import { SparkleGlyph } from '../../../../layouts/MainLayout/components/SparkleGlyph';
import { ACQ } from '../../../../theme/tokens';
import type { Promotion, CampaignTab, CampaignFilter, CampaignStatus } from '../../../../mock-data/types';
import {
  resolveCampaignStatus,
  formatStatusAt,
  useCampaignOverrides,
} from '../../../../utils/campaign-status';
// Card atoms reused from the acquisition Campaigns browser (period/thumb/title/chevron,
// LIVE pill, tab icon/count and the empty states).
import {
  TabIco,
  TabCount,
  TabDot,
  Period,
  Live,
  LiveDot,
  Main,
  Thumb,
  Chev,
  LoadMoreText,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyText,
  EmptyAction,
  EmptyActionText,
} from '../../../acquisition/components/Campaigns/Campaigns.styles';
// The browser chrome, shared with the acquisition Campaigns browser: both group the
// same way (tab row on the page, one header row in the card, search on its own row).
import {
  Browser,
  TabsBar,
  BrowserTab,
  BrowserTabScroll,
  BrowserCard,
  BrowserHead,
  FilterLbl,
  CategoryChips,
  CategoryPill,
  LiveTabDot,
  SearchSlot,
  SearchControl,
  SearchIconBtn,
  SearchDivider,
  SearchDismiss,
  SearchRow,
  SearchPill,
  SearchField,
  SearchClear,
  SearchResults,
} from '../../../acquisition/components/Campaigns/CampaignBrowser.styles';
import { CAMPAIGN_FILTER_LABELS } from '../../../acquisition/components/Campaigns/labels';
import { focusRecovery } from '../../../acquisition/components/Campaigns/browserFocus';
import {
  PromoList,
  PromoCard,
  CardLeft,
  CardHead,
  StatusGroup,
  Bullet,
  StatusInline,
  CardTitle,
  CardRight,
  BenefitsHeader,
  HeaderLine,
  HeaderLabel,
  BenefitsRow,
  Benefit,
  BenefitValue,
  BenefitLabel,
  StatusLabel,
  StatusTime,
  MoreButton,
  PromoTitleBox,
  JustActivated,
} from './Promotions.styles';

type TabId = CampaignTab['id'];

/** Ties the search toggle to the row it shows, via `aria-controls`. */
const SEARCH_ROW_ID = 'operations-promotions-search';

/** Promotion cards shown per page; "Load more" reveals one more page of the current tab. */
const PAGE_SIZE = 4;

/**
 * Per-tab empty-state icon shown when a tab genuinely holds no promotions (no
 * search / filter applied, that case shows the "no matches" variant instead).
 * Mirrors the acquisition Campaigns empty states. The title/body copy is built
 * (and translated) at the render site (see `emptyStateForTab`), so it stays
 * reactive to the live language switch.
 */
const EMPTY_ICON_BY_TAB: Record<TabId, IconName> = {
  // The Suggestions tab uses the branded Lumio glyph (SparkleGlyph) rather than
  // this monochrome icon; kept as the conceptual fallback for the same spark.
  suggestions: 'sparkle',
  running: 'trending-up',
  paused: 'pause',
  ended: 'stop',
};

const statusForTab = (id: TabId): CampaignStatus => (id === 'suggestions' ? 'suggestion' : id);

type TabTone = { dot: string | null; icon: 'pause' | 'stop' | null; badgeBg: string; badgeColor: string };
const tabTone = (tab: CampaignTab): TabTone => {
  if (tab.badgeColor === 'magenta') {
    // No dot: magenta is only ever the Suggestions tab, and that tab renders the Lumio
    // glyph instead, so a colour here would be unreachable. The badge still tints.
    return { dot: null, icon: null, badgeBg: ACQ.badgeSuggBg, badgeColor: ACQ.white };
  }
  if (tab.badgeColor === 'green') {
    return { dot: ACQ.badgeRunDot, icon: null, badgeBg: ACQ.badgeRunBg, badgeColor: ACQ.live };
  }
  return { dot: null, icon: tab.id === 'paused' ? 'pause' : 'stop', badgeBg: ACQ.badgeMutedBg, badgeColor: ACQ.t400 };
};

/**
 * The view is seeded ONCE from the URL query string (`?tab=&filter=&q=`), so a shared
 * link still opens to the right tab, category and search. It is not driven by the URL
 * after that: browsing does NOT navigate and does not update the URL, and Back does not
 * step through tab changes. See the note on the state declarations for why (a
 * same-document navigation snapped the scroll on iOS Safari). `?tab=` is validated
 * against the ids below and `?filter=` against the page's own `filters`; anything else
 * falls back to the default view ({@link DEFAULT_TAB}, no filter). Mirrors the acquisition Campaigns browser.
 */
const TAB_IDS: readonly TabId[] = ['suggestions', 'running', 'paused', 'ended'];
const isTabId = (value: string | null): value is TabId => value != null && (TAB_IDS as readonly string[]).includes(value);

/**
 * The tab the browser opens on: the promotions actually running for players
 * today. Matches the acquisition Campaigns browser; Suggestions stay one click
 * away (the tab row and the Running tab's own empty state both lead there).
 */
const DEFAULT_TAB: TabId = 'running';

export type PromotionsProps = {
  tabs: CampaignTab[];
  filters: CampaignFilter[];
  pool: Promotion[];
  /** Open a promotion — id read from `event.currentTarget.dataset.id`. */
  onOpenPromotion: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
};

/**
 * Operations promotions browser. The tab row sits on the page, above a card that
 * opens on a single header row: the "Category" chips (an "All" chip plus one per
 * category) on the left and the expanding search hard right. Below it, the
 * operations promotion card (period/thumb/title, then the +N Conversion /
 * Monetization / Retention activation benefits) and "Load more" pagination.
 */
export const Promotions = ({ tabs, filters, pool, onOpenPromotion }: PromotionsProps): React.ReactElement => {
  const { t } = useTranslation();
  // The Newton showcase shows no scores, so the cards leave out their Activation Benefits score deltas.
  const newtonShowcase = useAppSelector(selectNShowcase);
  const overrides = useCampaignOverrides();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const searchRef = React.useRef<HTMLInputElement>(null);
  // Focus lands back here whenever the search row unmounts: the element that had focus
  // goes with it, and without this the browser drops focus to <body>, so the next Tab
  // restarts at the top of the page instead of where the owner was (WCAG 2.4.3).
  const searchToggleRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  // Set by clearSearch, consumed by the layout effect that actually moves focus.
  const focusAfterClear = React.useRef(false);
  // Set by the inline clear and the category chips: unlike clearSearch they only unmount
  // themselves when they empty the view, so the effect gates this one on that transition.
  const focusAfterNarrow = React.useRef(false);

  // The promotion just activated from its suggested-detail page (passed via
  // navigation state on the redirect to the Running tab). Read once at mount: its
  // card gets the green hue (self-fading) and the "Just activated" tagline (which
  // lives until the owner navigates away — i.e. this component unmounts).
  const [justActivatedId] = React.useState<string | null>(
    () => (location.state as { activatedId?: string } | null)?.activatedId ?? null,
  );

  // Arriving from an activation, bring the table into view so the owner lands on
  // the just-activated card (on the Running tab) rather than the top of the page.
  // Deferred a frame so the page-transition mount has laid out. scrollIntoView uses
  // layout position (not the transition's transform), so it targets correctly.
  React.useEffect(() => {
    if (justActivatedId == null) return;
    const raf = requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, [justActivatedId]);

  // The promotions view (tab / category / search) is LOCAL state, seeded once from
  // the URL so a shared link still opens to the right view — but switching it does
  // NOT navigate. On mobile the document is the scroller, and a same-document
  // navigation makes iOS Safari re-reveal its toolbar and snap the scroll; driving
  // tabs from the URL therefore jumped the page on every click. Local state avoids
  // the navigation entirely. (Trade-off: the URL no longer updates as you browse.)
  const [tab, setTab] = React.useState<TabId>(() => {
    const raw = searchParams.get('tab');
    return isTabId(raw) ? raw : DEFAULT_TAB;
  });
  const [filter, setFilter] = React.useState<string | null>(() => {
    const raw = searchParams.get('filter');
    return raw && filters.some((f) => f.value === raw) ? raw : null;
  });
  const [query, setQueryState] = React.useState<string>(() => searchParams.get('q') ?? '');

  // Presentational-only view.
  const [searchOpen, setSearchOpen] = React.useState<boolean>(() => Boolean((searchParams.get('q') ?? '').trim()));
  const [pages, setPages] = React.useState(1);

  // Reconcile each promotion's stored status with any runtime pause/resume override
  // and the expiry rule (a once-run promotion past its deadline reads as ended), so
  // the tabs, counts and cards reflect the live state. `now` is read per render
  // (this list has no countdown to tick), so a pause/resume reflects on return and
  // an already-expired promotion shows as ended from first paint.
  const now = Date.now();
  const resolvedPool = React.useMemo<Promotion[]>(
    () =>
      pool.map((p) => {
        const override = overrides[p.id];
        const resolved = resolveCampaignStatus(p.status, p.expiresAt, override?.status, now);
        let statusAt = p.statusAt;
        if (resolved === 'ended' && p.status !== 'ended' && p.expiresAt != null) statusAt = formatStatusAt(p.expiresAt);
        else if (resolved === 'paused' && override?.status === 'paused') statusAt = formatStatusAt(override.at);
        return resolved === p.status && statusAt === p.statusAt ? p : { ...p, status: resolved, statusAt };
      }),
    [pool, overrides, now],
  );

  // Each tab's badge shows how many promotions actually carry its (resolved) status,
  // tallied straight from the pool — so the number can never drift from the rows.
  const counts = React.useMemo(() => {
    const tally: Record<CampaignStatus, number> = { suggestion: 0, running: 0, paused: 0, ended: 0 };
    for (const p of resolvedPool) tally[p.status] += 1;
    return tally;
  }, [resolvedPool]);

  // Anything that changes which rows are listed resets pagination to the first page.
  // Showing or hiding the search field does not: that changes what is on screen, not
  // which promotions match.
  const selectTab = (id: TabId): void => {
    setTab(id);
    setPages(1);
  };
  const selectFilter = (value: string | null): void => {
    setFilter(value);
    setPages(1);
    // Clearing to "All" can empty the view and unmount this chip; the effect recovers
    // focus, but only if that actually happens (it checks the empty-view transition).
    focusAfterNarrow.current = true;
  };
  const setQuery = (value: string): void => {
    setQueryState(value);
    setPages(1);
  };
  const loadMore = (): void => setPages((p) => p + 1);
  // Purely show/hide: hiding the field deliberately KEEPS the term, so collapsing
  // the row never silently changes which promotions are listed. What tells the
  // owner a hidden term is still filtering is the toggle's own active colour; the
  // inline clear inside the row is what actually drops the term.
  const toggleSearch = (): void => {
    // Hiding unmounts the row, and with it whatever inside it had focus, so pull
    // focus back to this toggle first. Deliberately in the handler and NOT inside the
    // setSearchOpen updater: React runs an updater during the render phase and may run
    // it more than once per dispatch, so a DOM call in there fires twice under
    // StrictMode and is not tied 1:1 to the click.
    if (searchOpen) searchToggleRef.current?.focus();
    setSearchOpen(!searchOpen);
  };
  // Leaves search altogether: drops the term and closes the field. Reached two ways,
  // the chip's dismiss and Escape inside the field, because both read as "I am done
  // searching" rather than "put this away for a moment". Distinct from the clear
  // inside the field, which only empties the text and keeps it open for the next
  // attempt, and from the toggle, which hides the field but deliberately keeps the
  // term applied.
  const clearSearch = (): void => {
    setQueryState('');
    setSearchOpen(false);
    setPages(1);
    // Both callers go away with the row they live in (the dismiss renders only while
    // a term is hidden; the field unmounts on close), so hand focus to the toggle
    // rather than letting it fall to <body>.
    //
    // Handed to the post-commit effect below rather than done here: dropping the term
    // can ALSO empty the view, and on a tab that only had matches because of the term
    // that makes isEmptyView true and unmounts the whole header row, toggle included.
    // Focusing synchronously targeted an element that was about to be removed, which
    // put focus on <body> (WCAG 2.4.3).
    focusAfterClear.current = true;
  };

  React.useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const term = query.trim();
  const q = term.toLowerCase();
  const status = statusForTab(tab);
  const matches = resolvedPool.filter(
    (p) => p.status === status && (!filter || p.category === filter) && (!q || p.title.toLowerCase().includes(q)),
  );
  // Surface a just-activated promotion at the top of its (Running) list so the
  // green hue + "Just activated" tagline are visible regardless of pool order or
  // pagination; otherwise the natural pool order stands.
  const activatedIdx = justActivatedId ? matches.findIndex((p) => p.id === justActivatedId) : -1;
  const ordered =
    activatedIdx > 0
      ? [matches[activatedIdx], ...matches.slice(0, activatedIdx), ...matches.slice(activatedIdx + 1)]
      : matches;
  const shown = ordered.slice(0, pages * PAGE_SIZE);
  const hasMore = shown.length < ordered.length;

  // The open tab holds nothing AND nothing of the owner's is hiding it, so the empty
  // message IS the whole view: the search box and category chips are suppressed, since
  // there is nothing there to search or narrow. Deliberately scoped to this exact
  // case rather than to `matches.length === 0`: when a search or filter emptied the
  // list, that same chrome is the only way back out of the state, and hiding it would
  // strand the owner on a view with no promotions and no control to restore them. The
  // condition reads the OPEN tab, not the pool, so a populated tab keeps its header
  // row while an empty sibling drops it.
  const isEmptyView = matches.length === 0 && !term && !filter;

  // Keeps keyboard focus on a real control whenever a header control tears down the row
  // it lives in, which would otherwise drop focus to <body> and restart the next Tab at
  // the top of the page (WCAG 2.4.3). Only ever acts on a USER action, via a flag one of
  // the handlers set: it must NOT react to a passive re-render. This browser reads a fresh
  // `now` per render but does not tick on a timer; its Campaigns twin does (useNow), where
  // a lone running campaign can expire out of the view with focus on <body>, and grabbing
  // focus on that tick would be a bug. Both guard the same way so the shared logic cannot
  // drift. Two flags, because the two cases want different targets:
  //  - clearSearch (Escape / the chip dismiss) always unmounts the field it ran from. On
  //    a populated tab the toggle beside it survives, so focus goes there; if the term
  //    was the only match the whole row went too, so it falls back to the open tab.
  //  - The inline clear or the "All"/category chip only removes itself when it EMPTIES the
  //    view (`becameEmpty`); a narrowing that leaves the row up is excluded, so focus is
  //    left on the chip the owner clicked. When it does empty, focus goes to the open tab.
  // Focus targets the OPEN tab via aria-pressed (the tab row outside the card always
  // survives), with preventScroll so recovery never moves the viewport. `wasEmptyView`
  // seeds from the initial value, so an already-empty first paint is not a transition.
  const wasEmptyView = React.useRef(isEmptyView);
  React.useLayoutEffect(() => {
    const becameEmpty = isEmptyView && !wasEmptyView.current;
    wasEmptyView.current = isEmptyView;
    const recovery = focusRecovery({
      afterClear: focusAfterClear.current,
      afterNarrow: focusAfterNarrow.current,
      becameEmpty,
    });
    focusAfterClear.current = false;
    focusAfterNarrow.current = false;
    if (recovery === 'none') return;
    const activeTab = panelRef.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"]');
    const toggle = searchToggleRef.current;
    // 'toggle-or-tab' prefers the surviving toggle; 'tab' and a gone toggle both land on
    // the open tab. preventScroll so recovery never moves the viewport.
    const target = recovery === 'toggle-or-tab' && toggle?.isConnected ? toggle : activeTab;
    target?.focus({ preventScroll: true });
  });

  // Tab / filter / benefit display labels are a fixed closed UI vocabulary sourced
  // from a shared label map (and the promotion benefits), reaching us as plain
  // strings. Localise them here at the render site (the shared maps stay English).
  const tabLabel = (id: TabId): string =>
    ({
      suggestions: t('Suggestions'),
      running: t('Running'),
      paused: t('Paused'),
      ended: t('Ended'),
    })[id];
  const FILTER_LABELS: Record<string, string> = {
    seasonal: t('Seasonal'),
    sports: t('Sports'),
    promo: t('Promo'),
    acquisition: t('Acquisition'),
    retention: t('Retention'),
  };
  const filterDisplay = (value: string): string => FILTER_LABELS[value] ?? CAMPAIGN_FILTER_LABELS[value] ?? value;
  const BENEFIT_LABELS: Record<string, string> = {
    Conversion: t('Conversion'),
    Monetization: t('Monetization'),
    Retention: t('Retention'),
  };
  const benefitLabel = (label: string): string => BENEFIT_LABELS[label] ?? label;

  // Per-tab empty-state copy, translated at the render site (reactive). The icon
  // comes from EMPTY_ICON_BY_TAB; Suggestions swaps in the branded Lumio glyph.
  const emptyStateForTab = (id: TabId): { title: string; body: string } =>
    ({
      suggestions: {
        title: t('New promotions are on the way'),
        body: t(
          'Lumio is analyzing your players and preparing fresh promotions to keep them playing and grow your revenue. They will appear here ready to launch.',
        ),
      },
      running: {
        title: t('No promotions are live'),
        body: t('Activate a suggested promotion to launch your first one, it shows up here while it runs.'),
      },
      paused: {
        title: t('Nothing paused'),
        body: t('Promotions you pause will wait here until you resume them.'),
      },
      ended: {
        title: t('No ended promotions'),
        body: t('Promotions are archived here once they finish.'),
      },
    })[id];

  const filterLabel = filter ? filterDisplay(filter) : '';
  const emptyMsg =
    filter && term
      ? t('No {{filter}} promotions match “{{term}}”.', { filter: filterLabel, term })
      : term
        ? t('No promotions match “{{term}}”.', { term })
        : filter
          ? t('No {{filter}} promotions in this view.', { filter: filterLabel })
          : t('No promotions in this view.');

  const onCardKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenPromotion(event);
    }
  };

  return (
    <Browser ref={panelRef}>
      <TabsBar>
        <BrowserTabScroll>
          {tabs.map((item) => {
            const tone = tabTone(item);
            const count = counts[statusForTab(item.id)];
            // The dot breathes only when something is actually live. A heartbeat over
            // "No promotions are live" advertises liveness the same screen denies, so an
            // empty tab keeps the plain dot.
            const Dot = count > 0 ? LiveTabDot : TabDot;
            return (
              <BrowserTab
                key={item.id}
                $active={tab === item.id}
                type="button"
                // A toggle button, like the category chips in the same row: the fill,
                // hairline and weight bump are all visual, so the open view is also
                // announced here. (The empty-view focus fallback finds the open tab
                // through this attribute.)
                aria-pressed={tab === item.id}
                onClick={() => selectTab(item.id)}
              >
                {item.id === 'suggestions' ? (
                  // Suggestions are Lumio's, so the tab is marked by the product's
                  // own glyph rather than a status dot (Figma node 9489:2066). The
                  // same glyph heads this tab's empty state below.
                  <TabIco>
                    <SparkleGlyph size={16} />
                  </TabIco>
                ) : tone.dot ? (
                  // Only Running still uses a dot (Suggestions took the Lumio glyph
                  // above), so this is the running-promotions heartbeat and nothing else.
                  <Dot $color={tone.dot} />
                ) : tone.icon ? (
                  <TabIco>
                    <Icon name={tone.icon} size={14} color={ACQ.t400} />
                  </TabIco>
                ) : null}
                <Text text={tabLabel(item.id)} tag="span" />
                <TabCount $bg={tone.badgeBg} $color={tone.badgeColor}>
                  <Text text={String(count)} tag="span" />
                </TabCount>
              </BrowserTab>
            );
          })}
        </BrowserTabScroll>
      </TabsBar>

      <BrowserCard>
        {isEmptyView ? null : (
          <BrowserHead>
            <FilterLbl text="Category" tag="span" />
            <CategoryChips>
              {/* "All" is the neutral state (no category applied) rather than a
                  category of its own, so it reads as pressed exactly when nothing
                  is filtering. It replaces the old "Clear filter" link: with the
                  chips always on screen there is no separate control to undo them. */}
              <CategoryPill
                $on={filter === null}
                type="button"
                aria-pressed={filter === null}
                onClick={() => selectFilter(null)}
              >
                <Text text="All" tag="span" />
              </CategoryPill>
              {filters.map((f) => (
                <CategoryPill
                  key={f.value}
                  $on={filter === f.value}
                  type="button"
                  aria-pressed={filter === f.value}
                  onClick={() => selectFilter(filter === f.value ? null : f.value)}
                >
                  <Text text={filterDisplay(f.value)} tag="span" />
                </CategoryPill>
              ))}
            </CategoryChips>
            <SearchSlot>
              <SearchControl $on={Boolean(term)}>
                <SearchIconBtn
                  ref={searchToggleRef}
                  type="button"
                  aria-label={searchOpen ? t('Hide search') : t('Search promotions')}
                  aria-expanded={searchOpen}
                  aria-controls={SEARCH_ROW_ID}
                  onClick={toggleSearch}
                >
                  <Icon name="search" size={16} />
                </SearchIconBtn>
                {!searchOpen && term ? (
                  // Offered exactly when the field is HIDDEN but a term is still
                  // filtering: that is the state with no other way out, since
                  // hiding deliberately keeps the term and the clear inside the
                  // field went off screen with it. While the field is open that
                  // inline clear is right there, so a second one here would be
                  // redundant.
                  <>
                    <SearchDivider aria-hidden="true" />
                    <SearchDismiss type="button" aria-label={t('Clear search')} onClick={clearSearch}>
                      <Icon name="close" size={16} />
                    </SearchDismiss>
                  </>
                ) : null}
              </SearchControl>
            </SearchSlot>
          </BrowserHead>
        )}

        {!isEmptyView && searchOpen ? (
          <SearchRow id={SEARCH_ROW_ID}>
            <SearchPill>
              <Icon name="search" size={18} />
              <SearchField
                ref={searchRef}
                type="text"
                aria-label={t('Search promotions')}
                placeholder={t('Search promotions\u2026')}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  // Escape LEAVES search (drops the term), rather than merely hiding
                  // the row the way the toggle does: dismissing a field you are typing
                  // in means you are done with the filter, so leaving it silently
                  // applied behind a collapsed row is the wrong default.
                  if (event.key === 'Escape') clearSearch();
                }}
              />
              {query ? (
                <SearchClear
                  type="button"
                  aria-label={t('Clear search text')}
                  onClick={() => {
                    setQuery('');
                    // Normally the field stays and keeps focus; if clearing empties the
                    // view the field unmounts, and the effect moves focus to the tab.
                    focusAfterNarrow.current = true;
                    searchRef.current?.focus();
                  }}
                >
                  <Icon name="close" size={14} />
                </SearchClear>
              ) : null}
            </SearchPill>
            {term ? (
              // Counted off `matches`, i.e. every promotion the term found, not the
              // `shown` page of them: the pager below reveals the rest. `count` is
              // i18next's plural trigger, so this reads "1 result" in the singular.
              <SearchResults text="{{count}} results" placeholders={{ count: matches.length }} tag="span" />
            ) : null}
          </SearchRow>
        ) : null}

        {matches.length > 0 ? (
          <PromoList>
            {shown.map((promo) => (
              <PromoCard
                key={promo.id}
                $clickable
                $justActivated={promo.id === justActivatedId}
                data-id={promo.id}
                role="button"
                tabIndex={0}
                aria-label={t('Open {{name}}', { name: promo.title })}
                onClick={onOpenPromotion}
                onKeyDown={onCardKeyDown}
              >
                <CardLeft>
                  <CardHead>
                    <Period text="Campaign period: {{period}}" placeholders={{ period: promo.period }} tag="span" />
                    {promo.status === 'running' ? (
                      <StatusGroup>
                        <Bullet />
                        <Live>
                          <LiveDot />
                          <Text text="LIVE" tag="span" />
                        </Live>
                      </StatusGroup>
                    ) : (promo.status === 'paused' || promo.status === 'ended') && promo.statusAt ? (
                      <StatusGroup>
                        <Bullet />
                        <StatusInline>
                          <StatusLabel text={promo.status === 'paused' ? t('Paused on') : t('Ended on')} tag="span" />
                          <StatusTime text={promo.statusAt} tag="span" />
                        </StatusInline>
                      </StatusGroup>
                    ) : null}
                  </CardHead>
                  <Main>
                    <Thumb src={promo.image} alt="" />
                    <PromoTitleBox>
                      <CardTitle text={promo.title} tag="span" />
                      {promo.id === justActivatedId ? (
                        <JustActivated text="Just activated" tag="span" />
                      ) : null}
                    </PromoTitleBox>
                    <Chev aria-hidden>
                      <Icon name="arrow-right-circle" size={24} color={ACQ.t50} />
                    </Chev>
                  </Main>
                </CardLeft>

                {newtonShowcase ? null : (
                  <CardRight>
                    <BenefitsHeader>
                      <HeaderLine />
                      <HeaderLabel text="Activation Benefits" tag="span" />
                      <HeaderLine />
                    </BenefitsHeader>
                    <BenefitsRow>
                      {promo.benefits.map((benefit) => (
                        <Benefit key={benefit.id}>
                          <BenefitValue text={benefit.value} tag="span" />
                          <BenefitLabel text={benefitLabel(benefit.label)} tag="span" />
                        </Benefit>
                      ))}
                    </BenefitsRow>
                  </CardRight>
                )}
              </PromoCard>
            ))}
            {hasMore ? (
              <MoreButton type="button" onClick={loadMore}>
                <Icon name="plus-circle" size={20} color={ACQ.white} />
                <LoadMoreText text="Load more promotions" tag="span" />
              </MoreButton>
            ) : null}
          </PromoList>
        ) : term || filter ? (
          // A search/category is on but nothing matched it — a "no results" state
          // distinct from a genuinely empty tab, and the reason the header row above
          // is NOT suppressed here. The ways back all live in that row: the "All"
          // chip, the clear inside the search field, and the chip's dismiss.
          <Empty>
            <EmptyIcon>
              <Icon name="search" size={24} color={ACQ.t400} />
            </EmptyIcon>
            <EmptyTitle text="No matches" tag="span" />
            <EmptyText text={emptyMsg} tag="span" />
          </Empty>
        ) : (
          // The tab itself is empty — tailored copy for this state.
          <Empty>
            <EmptyIcon>
              {tab === 'suggestions' ? (
                <SparkleGlyph size={30} />
              ) : (
                <Icon name={EMPTY_ICON_BY_TAB[tab]} size={24} color={ACQ.t400} />
              )}
            </EmptyIcon>
            <EmptyTitle text={emptyStateForTab(tab).title} tag="span" />
            <EmptyText text={emptyStateForTab(tab).body} tag="span" />
            {/* Offered only when there is actually something to browse: with no
                suggestions waiting, this button sent the owner from one empty tab to
                another, which reads as a broken control rather than a way forward. The
                count is the same tally the Suggestions badge shows, so the two can
                never disagree. */}
            {tab === 'running' && counts.suggestion > 0 ? (
              <EmptyAction type="button" onClick={() => selectTab('suggestions')}>
                <Icon name="sparkle" size={16} color={ACQ.orange} />
                <EmptyActionText text="Browse suggestions" tag="span" />
              </EmptyAction>
            ) : null}
          </Empty>
        )}
      </BrowserCard>
    </Browser>
  );
};

export default Promotions;
